import { Component, OnInit, inject } from '@angular/core';
import {TranslationService} from '../../core/services/translation.service';
import {PaymentService} from '../../data/services/payment.service';
import {HupSubscriptionsService} from '../../data/services/hup-subscriptions.service';
import {HupSubscription} from '../../data/models/subscription.model';
import {Payment} from '../../data/models/payment.model';
import {NavigationCommand, NavigationService} from "../../data/services/navigation.service";
import {LoadingSpinnerComponent} from "../../core/components/loading-spinner/loading-spinner.component";

import {GeneralErrorsComponent} from "../../core/components/general-errors/general-errors.component";
import {MatCard, MatCardContent, MatCardTitle} from "@angular/material/card";
import {FaIconLibrary, FontAwesomeModule} from "@fortawesome/angular-fontawesome";
import {MatButton} from "@angular/material/button";
import {NgbTooltip} from "@ng-bootstrap/ng-bootstrap";
import {BankAccountPipe} from "../../core/pipes/bank-account.pipe";
import {faHourglass} from "@fortawesome/free-solid-svg-icons/faHourglass";
import {combineLatest, mergeMap, Observable, of} from "rxjs";
import {catchError, map} from "rxjs/operators";
import {ConfigService} from "../../core/services/config.service";
import {ApplyIfNotEmptyDirective} from "../../core/directives/apply-if-not-empty.directive";
import {OnlinePaymentChecker} from "../../core/components/utils/online-payment-checker";


@Component({
  selector: 'app-payment',
  templateUrl: './payment.component.html',
  styleUrls: ['./payment.component.scss'],
  imports: [
    LoadingSpinnerComponent,
    GeneralErrorsComponent,
    MatCard,
    MatCardTitle,
    MatCardContent,
    FontAwesomeModule,
    MatButton,
    NgbTooltip,
    BankAccountPipe,
    ApplyIfNotEmptyDirective
  ],
  standalone: true
})
export class PaymentComponent implements OnInit {
  private configService = inject(ConfigService);
  private paymentService = inject(PaymentService);
  private hupSubscriptionsService = inject(HupSubscriptionsService);
  private navigationService = inject(NavigationService);
  translationService = inject(TranslationService);
  faLibrary = inject(FaIconLibrary);

  private onlinePaymentChecker: OnlinePaymentChecker = new OnlinePaymentChecker();

  isLoadingPayments: boolean = false;
  isLoadingSubs: boolean = false;
  errorsArr: string[] = [];
  displayPaymentChange: boolean = true;

  subs: HupSubscription[];
  payments: Payment[];

  constructor() {
    const faLibrary = this.faLibrary;

    faLibrary.addIcons(
      faHourglass
    )
  }

  ngOnInit(): void {
    this.isLoadingSubs = true;
    this.isLoadingPayments = true;

    const onlinePaymentConfig = this.configService.getConfig(this.onlinePaymentChecker.ONLINE_PAYMENT_METHOD_ID_CONFIG_KEY);
    if (onlinePaymentConfig && onlinePaymentConfig.value) {
      this.onlinePaymentChecker.setPaymentCode(onlinePaymentConfig.value);
    }

    this.hupSubscriptionsService.getSubscriptions()
      .pipe(
        mergeMap(subs => {
          this.subs = subs;
          this.isLoadingSubs = false;
          return combineLatest([of(subs), this.getFilteredBanks(subs)])
        }),
        catchError((error) => {
          this.errorsArr.push(this.translationService.getTranslation('error_subscription_init'));
          return of(null);
        })
      )
      .subscribe(res => {
        this.payments = res[1];
        this.isLoadingPayments = false;
      });

    this.displayPaymentChange = this.configService.getConfig('payment.changeOfPaymentConditionsAllowed')?.asBoolean();
  }

  getFilteredBanks(subs: HupSubscription[]) : Observable<Payment[]> {
    return this.paymentService.getPayments().pipe(
      map(
        ps => {
          let usedBanks: number[] = subs
            .map(s => s.curPayment?.bank?.backendId?? -1);
          return ps.filter(p => (p.bank.backendId ?? 1) <= 0 || usedBanks.includes(p.bank.backendId));
        }
      ),
      catchError((error) => {
        this.errorsArr.push(this.translationService.getTranslation('error_payment_init'));
        return of(null);
      })
    );
  }

  subPaymentEdit(sub: HupSubscription): void {
    this.navigationService.navigateTo(NavigationCommand.SUBPAYMENTEDIT, [sub.webId+'', sub.backendId+''])
  }

  paymentEdit(payment: Payment): void {
    this.navigationService.navigateTo(NavigationCommand.PAYMENTEDIT, [payment.webId+'', payment.bank.backendId+''])
  }

  isOnlinePayment(payment: Payment): boolean {
   return this.onlinePaymentChecker.isOnlinePayment(payment);
  }
}
